// import { decodeTime, isValid } from 'ulidx';

// export function ulidToDate(ulid: string): string {
//   // if (!isValid(ulid)) {
//   //   throw new Error('Invalid ULID length');
//   // }
//   // const timestamp = decodeTime(ulid);

//   // ULID timestamps are in milliseconds
//   const date = new Date(timestamp);
//   return date.toISOString();
// }

import { AxiosStatic } from 'axios';
import axiosRetry from 'axios-retry';
import CryptoJS from 'crypto-js';
import { orderBy, get } from 'lodash';
import {
  Big,
  hasStockForUnits,
  roundFinalMoney,
  roundUnitCost,
} from './big-helpers';
import {
  atomsFromStock,
  AtomicFraction,
  buildFractionRowsFromList,
  decomposeAtoms,
  DecomposeOptions,
} from './inventory-atoms';

import {
  BreakdownItem,
  Debt,
  DebtAggregate,
  DebtorGroup,
  DebtStatus,
  DynamoDBItem,
  Language,
  UiLanguage,
} from '@bofrak-backend/shared';

/**
 * Calculates the inventory breakdown into parent and child units using
 * integer-atom math. Each row's `quantity_in_unit_package` is the single
 * source of truth — the decimal `fraction` field is preserved for output
 * but not used for arithmetic. This guarantees the same stock value
 * decomposes identically across every screen, regardless of whether the
 * stored `fraction` is in sync with `quantity_in_unit_package`.
 *
 * @param inventory   Consolidated inventory value (stock in parent units).
 * @param fractions   Parent + child rows (in any order).
 * @param parentUnit  Optional override for the parent row's unit label.
 * @param options     `{ signed: true }` to allow negative totals (skip the
 *                    legacy `inventory <= 0` early return) for delta math.
 */
export const calculateInventoryBreakdown = (
  inventory: number,
  fractions: ReadonlyArray<AtomicFraction>,
  parentUnit?: string | number,
  options: DecomposeOptions = {},
): BreakdownItem[] => {
  if (!fractions || fractions.length === 0) return [];
  if (!options.signed && inventory <= 0) return [];

  const rows = buildFractionRowsFromList(fractions, parentUnit);
  if (rows.length === 0) return [];

  const parent = rows[0];
  const atoms = atomsFromStock(inventory, {
    quantity_in_unit_package: parent.qty_in_package,
  });
  return decomposeAtoms(atoms, rows, options);
};

export function removeBracesAndBrackets(value: string): string {
  return value.replace(/[{}[\]()]/g, '');
}

export function removeExtraBraces(input: string): string {
  return input.replace(/\(+([^()]+)\)+/g, '($1)');
}

export function sortDebtorGroups(groups: DebtorGroup[]): DebtorGroup[] {
  // 1. Define status priority
  const statusOrder: DebtStatus[] = [
    DebtStatus.OVERDUE,
    DebtStatus.ACTIVE,
    DebtStatus.DEFAULTED,
    DebtStatus.CANCELLED,
    DebtStatus.TRANSFERRED,
    DebtStatus.CLOSED,
  ];

  // Helper: given a debt, return a tuple [statusIndex, dueTimestamp]
  function debtKey(debt: DebtorGroup['debts'][0]): [number, number] {
    const statusIdx = statusOrder.indexOf(debt.status);
    const dueTs = debt.next_due_date
      ? Date.parse(debt.next_due_date)
      : Infinity;
    return [statusIdx, dueTs];
  }

  return groups
    .slice() // don’t mutate original
    .sort((A, B) => {
      // 2. Find each group’s “minimum” debt by our key
      const bestA = A.debts.reduce(
        (min, d) => {
          const k = debtKey(d);
          return k[0] < min[0] || (k[0] === min[0] && k[1] < min[1]) ? k : min;
        },
        // initialize with worst possible: status beyond known + infinite date
        [statusOrder.length, Infinity] as [number, number],
      );
      const bestB = B.debts.reduce(
        (min, d) => {
          const k = debtKey(d);
          return k[0] < min[0] || (k[0] === min[0] && k[1] < min[1]) ? k : min;
        },
        [statusOrder.length, Infinity] as [number, number],
      );

      // 3. Compare group keys
      if (bestA[0] !== bestB[0]) {
        return bestA[0] - bestB[0];
      }
      return bestA[1] - bestB[1];
    });
}

export function sortDebtAggregates(
  aggregates: DebtAggregate[],
): DebtAggregate[] {
  // 1. same status priority as before
  const statusOrder: DebtStatus[] = [
    DebtStatus.OVERDUE,
    DebtStatus.ACTIVE,
    DebtStatus.DEFAULTED,
    DebtStatus.CANCELLED,
    DebtStatus.TRANSFERRED,
    DebtStatus.CLOSED,
  ];

  return aggregates
    .slice() // don’t mutate the original
    .sort((aAgg, bAgg) => {
      const a = aAgg.debt;
      const b = bAgg.debt;

      // 2a. compare status by our custom order
      const idxA = statusOrder.indexOf(a.status);
      const idxB = statusOrder.indexOf(b.status);
      if (idxA !== idxB) {
        return idxA - idxB;
      }

      // 2b. same status → oldest next_due_date first
      const timeA = a.next_due_date ? Date.parse(a.next_due_date) : Infinity;
      const timeB = b.next_due_date ? Date.parse(b.next_due_date) : Infinity;
      return timeA - timeB;
    });
}

export function sortDebts(debts: Debt[]): Debt[] {
  // 1. Define the desired status order
  const statusOrder: DebtStatus[] = [
    DebtStatus.OVERDUE,
    DebtStatus.ACTIVE,
    DebtStatus.DEFAULTED,
    DebtStatus.CANCELLED,
    DebtStatus.TRANSFERRED,
    DebtStatus.CLOSED,
  ];

  // 2. Return a new, sorted array
  return debts
    .slice() // work on a shallow copy so we don’t mutate the original
    .sort((a, b) => {
      // 2a. Compare status according to our order
      const idxA = statusOrder.indexOf(a.status);
      const idxB = statusOrder.indexOf(b.status);
      if (idxA !== idxB) {
        return idxA - idxB;
      }

      // 2b. Same status → compare next_due_date ascending
      //    Treat null/undefined or unparsable dates as “Infinity” so they sort last
      const timeA = a.next_due_date ? Date.parse(a.next_due_date) : Infinity;
      const timeB = b.next_due_date ? Date.parse(b.next_due_date) : Infinity;
      return timeA - timeB;
    });
}

export function removeDynamoDBKeys<T>(item: DynamoDBItem) {
  const shouldLog = false;
  const {
    PK,
    SK,
    GSI1PK,
    GSI1SK,
    GSI2PK,
    GSI2SK,
    GSI3PK,
    GSI3SK,
    GSI4PK,
    GSI4SK,
    GSI5PK,
    GSI5SK,
    GSI6PK,
    GSI6SK,
    GSI7PK,
    GSI7SK,
    EntityIndexPK,
    EntityIndexSK,
    ...rest
  } = item;

  if (shouldLog)
    console.log('Removed DynamoDB keys:', {
      PK,
      SK,
      GSI1PK,
      GSI1SK,
      GSI2PK,
      GSI2SK,
      GSI3PK,
      GSI3SK,
      GSI4PK,
      GSI4SK,
      GSI5PK,
      GSI5SK,
      GSI6PK,
      GSI6SK,
      GSI7PK,
      GSI7SK,
      EntityIndexPK,
      EntityIndexSK,
    });

  return rest as T;
}

/**
 * Masks region and account number in an AWS ARN.
 *
 * Example:
 * arn:aws:lambda:eu-west-1:123456789012:function:my-fn
 * → arn:aws:lambda:eu-west-1:XXXXXX:function:my-fn
 */
export const maskAwsArn = (arn: string): string => {
  const arnParts = arn.split(':');

  // Basic ARN validation
  if (arnParts.length < 6 || arnParts[0] !== 'arn') {
    return arn; // return original if not a valid ARN
  }

  // arn:partition:service:region:account-id:resource
  arnParts[4] = '***'; // account ID

  return arnParts.join(':');
};

export function convertToSentence(s: string): string {
  if (!s) return '';

  let words: string[];

  // Check for snake_case or SCREAMING_SNAKE_CASE
  if (s.includes('_')) {
    words = s.split('_');
  }
  // Check for kebab-case
  else if (s.includes('-')) {
    words = s.split('-');
  }
  // Handle camelCase or PascalCase by inserting spaces before uppercase letters following lowercase letters.
  else {
    s = s.replace(/([a-z])([A-Z])/g, '$1 $2');
    words = s.split(' ');
  }

  // Capitalize the first letter of each word (rest in lowercase)
  const sentenceWords = words
    .filter((word) => word.length > 0)
    .map((word) => {
      word = word.toLowerCase();
      return word.charAt(0).toUpperCase() + word.slice(1);
    });

  return sentenceWords.join(' ');
}

export function isProductForSale({
  fraction,
  inventory,
  is_available_for_sale,
  is_available_in_store,
}: {
  is_available_for_sale: boolean;
  inventory: number;
  fraction: number;
  is_available_in_store: boolean;
  is_sold_online?: boolean;
}): { is_available_for_sale: boolean; reason: string } {
  const hasUnit = hasStockForUnits(inventory, fraction);
  const is_available_for_sale1 =
    is_available_for_sale && hasUnit && is_available_in_store;
  // &&is_sold_online;
  let reason = '';

  if (!is_available_for_sale) {
    reason = 'Not enough inventory or price is lower than cost';
  } else if (!is_available_in_store) {
    reason = 'Product cannot be sold in this store';
    // } else if (!is_sold_online) {
    //  reason = 'Product is not sold online';
  } else if (!hasUnit) {
    reason = 'Product inventory is less than its unit quantity';
  }

  return { is_available_for_sale: is_available_for_sale1, reason };
}

export interface SanitizePrimitivesOptions {
  /**
   * When false, digit-only strings (e.g. phone numbers, IDs) are left as
   * strings instead of being coerced to numbers. Defaults to true to keep
   * the legacy behavior.
   */
  convertNumbers?: boolean;
}

export function sanitizePrimitives<T>(
  obj: T,
  options: SanitizePrimitivesOptions = {},
): T {
  const { convertNumbers = true } = options;

  const isPlainObject = (val: any): val is Record<string, any> =>
    typeof val === 'object' && val !== null && !Array.isArray(val);

  const sanitize = (value: any): any => {
    // Remove null / undefined
    if (value === null || value === undefined) {
      return undefined;
    }

    // Handle strings
    if (typeof value === 'string') {
      const trimmed = value.trim();
      const lower = trimmed.toLowerCase();

      // Remove string versions of null / undefined
      if (lower === 'null' || lower === 'undefined' || trimmed === '') {
        return undefined;
      }

      // Boolean conversion
      if (lower === 'true') return true;
      if (lower === 'false') return false;

      // Strict numeric conversion (no hex, no scientific notation)
      if (convertNumbers && /^-?\d+(\.\d+)?$/.test(trimmed)) {
        return Number(trimmed);
      }

      return value;
    }

    // Arrays: sanitize elements + remove undefined entries
    if (Array.isArray(value)) {
      return value.map(sanitize).filter((v) => v !== undefined);
    }

    // Objects: sanitize values + drop undefined keys
    if (isPlainObject(value)) {
      return Object.entries(value).reduce<Record<string, any>>(
        (acc, [key, val]) => {
          const sanitized = sanitize(val);
          if (sanitized !== undefined) {
            acc[key] = sanitized;
          }
          return acc;
        },
        {},
      );
    }

    // Booleans, numbers, etc.
    return value;
  };

  return sanitize(obj);
}

export function removeNullsAndUndefined<T>(input: T): T {
  if (Array.isArray(input)) {
    // Process each element in the array recursively, and filter out null/undefined
    return input
      .map(removeNullsAndUndefined)
      .filter((item) => item !== null && item !== undefined) as unknown as T;
  } else if (input !== null && typeof input === 'object') {
    // For objects, process each key/value
    const result: any = {};
    Object.entries(input).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        const cleanedValue = removeNullsAndUndefined(value);
        // Only assign if result is not null/undefined
        if (cleanedValue !== null && cleanedValue !== undefined) {
          result[key] = cleanedValue;
        }
      }
    });
    return result;
  }
  // For primitives (string, number, boolean, symbol, bigint), return as is
  return input;
}

/**
 * Derives a fraction (child) product's cost from its parent's cost.
 *
 * The derived cost is a per-unit RATE, rounded half-up to the unit-cost
 * precision (6 dp) — never to whole units or cents, whose truncation error
 * is amplified by the child's stock in every inventory valuation.
 * Computing through Big makes every derivation site agree bit-for-bit
 * (no float noise flipping a rounding boundary) and gives replayed
 * events a stable value to compare against.
 */
export function deriveChildCost(
  parentCost: number,
  childQtyInPackage: number,
  parentQtyInPackage: number,
): number {
  const childQty = Math.round(childQtyInPackage) || 1;
  const parentQty = Math.round(parentQtyInPackage) || 1;
  return roundUnitCost(new Big(parentCost || 0).times(childQty).div(parentQty));
}

export function calculateWeightedCost({
  currentQuantity,
  currentCost,
  newQuantity,
  newCost,
}: {
  currentQuantity: number;
  currentCost: number;
  newQuantity: number;
  newCost: number;
}): number {
  const safeCurrentQty = Math.max(currentQuantity, 0);

  // Calculate the total cost of current and new stock using Big
  const totalCurrentCost = new Big(safeCurrentQty).times(currentCost);
  const totalNewCost = new Big(newQuantity).times(newCost);
  const totalCost = totalCurrentCost.plus(totalNewCost);

  // Calculate the total quantity
  const totalQuantity = new Big(safeCurrentQty).plus(newQuantity);

  if (totalQuantity.eq(0)) {
    return 0; // Avoid division by zero; return 0 cost if no quantity
  }

  // The weighted average is a per-unit RATE: keep it at the 6 dp unit-cost
  // precision so that quantity × cost recomputes the pooled value to well
  // under a cent (a 2 dp cost drifts by up to 0.005 × quantity).
  return roundUnitCost(totalCost.div(totalQuantity));
}

export function getReceiptNumber(receiptId: string): number {
  return parseInt(receiptId.split('-').pop()!);
}

// Helper function to encrypt credentials
export const encryptString = (toEncrypt: string, key: string): string => {
  return CryptoJS.AES.encrypt(toEncrypt, key).toString();
};

// Helper function to decrypt credentials
export const decryptString = (encrypted: string, key: string): string => {
  try {
    const bytes = CryptoJS.AES.decrypt(encrypted, key);
    const decrypted = bytes.toString(CryptoJS.enc.Utf8);
    return decrypted;
  } catch (error) {
    console.error(error);
  }

  return 'Failed to decrypt credentials. Data might be corrupted or invalid.';
};

export function constructPassword({
  phone_number,
  pin,
}: {
  phone_number: string;
  pin: string;
}) {
  return `ShopNSmile${phone_number.replace('+', '')}${pin}`;
}

// ── Country Phone Data ───────────────────────────────────

export interface CountryPhoneData {
  /** ISO 3166-1 alpha-2 code (e.g. "ZM") */
  code: string;
  /** Display name (e.g. "Zambia") */
  name: string;
  /** Dial code including + (e.g. "+260") */
  dialCode: string;
  /** Flag emoji (e.g. "🇿🇲") */
  flag: string;
  /** Numeric country calling code (e.g. "260") */
  numericCode: string;
}

/**
 * Comprehensive list of supported countries with phone dialing info.
 * African countries first, then Middle East, Asia, Europe, Americas, Oceania.
 */
export const SUPPORTED_COUNTRIES: CountryPhoneData[] = [
  // ── Africa ──
  {
    code: 'ZM',
    name: 'Zambia',
    dialCode: '+260',
    flag: '🇿🇲',
    numericCode: '260',
  },
  {
    code: 'MZ',
    name: 'Mozambique',
    dialCode: '+258',
    flag: '🇲🇿',
    numericCode: '258',
  },
  {
    code: 'ZA',
    name: 'South Africa',
    dialCode: '+27',
    flag: '🇿🇦',
    numericCode: '27',
  },
  {
    code: 'ZW',
    name: 'Zimbabwe',
    dialCode: '+263',
    flag: '🇿🇼',
    numericCode: '263',
  },
  {
    code: 'MW',
    name: 'Malawi',
    dialCode: '+265',
    flag: '🇲🇼',
    numericCode: '265',
  },
  {
    code: 'TZ',
    name: 'Tanzania',
    dialCode: '+255',
    flag: '🇹🇿',
    numericCode: '255',
  },
  {
    code: 'KE',
    name: 'Kenya',
    dialCode: '+254',
    flag: '🇰🇪',
    numericCode: '254',
  },
  {
    code: 'UG',
    name: 'Uganda',
    dialCode: '+256',
    flag: '🇺🇬',
    numericCode: '256',
  },
  {
    code: 'RW',
    name: 'Rwanda',
    dialCode: '+250',
    flag: '🇷🇼',
    numericCode: '250',
  },
  {
    code: 'BI',
    name: 'Burundi',
    dialCode: '+257',
    flag: '🇧🇮',
    numericCode: '257',
  },
  {
    code: 'CD',
    name: 'Congo (DRC)',
    dialCode: '+243',
    flag: '🇨🇩',
    numericCode: '243',
  },
  {
    code: 'CG',
    name: 'Congo (Republic)',
    dialCode: '+242',
    flag: '🇨🇬',
    numericCode: '242',
  },
  {
    code: 'AO',
    name: 'Angola',
    dialCode: '+244',
    flag: '🇦🇴',
    numericCode: '244',
  },
  {
    code: 'NA',
    name: 'Namibia',
    dialCode: '+264',
    flag: '🇳🇦',
    numericCode: '264',
  },
  {
    code: 'BW',
    name: 'Botswana',
    dialCode: '+267',
    flag: '🇧🇼',
    numericCode: '267',
  },
  {
    code: 'SZ',
    name: 'Eswatini',
    dialCode: '+268',
    flag: '🇸🇿',
    numericCode: '268',
  },
  {
    code: 'LS',
    name: 'Lesotho',
    dialCode: '+266',
    flag: '🇱🇸',
    numericCode: '266',
  },
  {
    code: 'MG',
    name: 'Madagascar',
    dialCode: '+261',
    flag: '🇲🇬',
    numericCode: '261',
  },
  {
    code: 'MU',
    name: 'Mauritius',
    dialCode: '+230',
    flag: '🇲🇺',
    numericCode: '230',
  },
  {
    code: 'SC',
    name: 'Seychelles',
    dialCode: '+248',
    flag: '🇸🇨',
    numericCode: '248',
  },
  {
    code: 'ET',
    name: 'Ethiopia',
    dialCode: '+251',
    flag: '🇪🇹',
    numericCode: '251',
  },
  {
    code: 'SO',
    name: 'Somalia',
    dialCode: '+252',
    flag: '🇸🇴',
    numericCode: '252',
  },
  {
    code: 'SD',
    name: 'Sudan',
    dialCode: '+249',
    flag: '🇸🇩',
    numericCode: '249',
  },
  {
    code: 'SS',
    name: 'South Sudan',
    dialCode: '+211',
    flag: '🇸🇸',
    numericCode: '211',
  },
  { code: 'EG', name: 'Egypt', dialCode: '+20', flag: '🇪🇬', numericCode: '20' },
  {
    code: 'LY',
    name: 'Libya',
    dialCode: '+218',
    flag: '🇱🇾',
    numericCode: '218',
  },
  {
    code: 'TN',
    name: 'Tunisia',
    dialCode: '+216',
    flag: '🇹🇳',
    numericCode: '216',
  },
  {
    code: 'DZ',
    name: 'Algeria',
    dialCode: '+213',
    flag: '🇩🇿',
    numericCode: '213',
  },
  {
    code: 'MA',
    name: 'Morocco',
    dialCode: '+212',
    flag: '🇲🇦',
    numericCode: '212',
  },
  {
    code: 'MR',
    name: 'Mauritania',
    dialCode: '+222',
    flag: '🇲🇷',
    numericCode: '222',
  },
  {
    code: 'SN',
    name: 'Senegal',
    dialCode: '+221',
    flag: '🇸🇳',
    numericCode: '221',
  },
  {
    code: 'ML',
    name: 'Mali',
    dialCode: '+223',
    flag: '🇲🇱',
    numericCode: '223',
  },
  {
    code: 'GN',
    name: 'Guinea',
    dialCode: '+224',
    flag: '🇬🇳',
    numericCode: '224',
  },
  {
    code: 'CI',
    name: "Côte d'Ivoire",
    dialCode: '+225',
    flag: '🇨🇮',
    numericCode: '225',
  },
  {
    code: 'BF',
    name: 'Burkina Faso',
    dialCode: '+226',
    flag: '🇧🇫',
    numericCode: '226',
  },
  {
    code: 'NE',
    name: 'Niger',
    dialCode: '+227',
    flag: '🇳🇪',
    numericCode: '227',
  },
  {
    code: 'TG',
    name: 'Togo',
    dialCode: '+228',
    flag: '🇹🇬',
    numericCode: '228',
  },
  {
    code: 'BJ',
    name: 'Benin',
    dialCode: '+229',
    flag: '🇧🇯',
    numericCode: '229',
  },
  {
    code: 'NG',
    name: 'Nigeria',
    dialCode: '+234',
    flag: '🇳🇬',
    numericCode: '234',
  },
  {
    code: 'GH',
    name: 'Ghana',
    dialCode: '+233',
    flag: '🇬🇭',
    numericCode: '233',
  },
  {
    code: 'CM',
    name: 'Cameroon',
    dialCode: '+237',
    flag: '🇨🇲',
    numericCode: '237',
  },
  {
    code: 'GA',
    name: 'Gabon',
    dialCode: '+241',
    flag: '🇬🇦',
    numericCode: '241',
  },
  {
    code: 'GQ',
    name: 'Equatorial Guinea',
    dialCode: '+240',
    flag: '🇬🇶',
    numericCode: '240',
  },
  {
    code: 'TD',
    name: 'Chad',
    dialCode: '+235',
    flag: '🇹🇩',
    numericCode: '235',
  },
  {
    code: 'CF',
    name: 'Central African Republic',
    dialCode: '+236',
    flag: '🇨🇫',
    numericCode: '236',
  },
  {
    code: 'CV',
    name: 'Cape Verde',
    dialCode: '+238',
    flag: '🇨🇻',
    numericCode: '238',
  },
  {
    code: 'ST',
    name: 'São Tomé and Príncipe',
    dialCode: '+239',
    flag: '🇸🇹',
    numericCode: '239',
  },
  {
    code: 'GW',
    name: 'Guinea-Bissau',
    dialCode: '+245',
    flag: '🇬🇼',
    numericCode: '245',
  },
  {
    code: 'SL',
    name: 'Sierra Leone',
    dialCode: '+232',
    flag: '🇸🇱',
    numericCode: '232',
  },
  {
    code: 'LR',
    name: 'Liberia',
    dialCode: '+231',
    flag: '🇱🇷',
    numericCode: '231',
  },
  {
    code: 'GM',
    name: 'Gambia',
    dialCode: '+220',
    flag: '🇬🇲',
    numericCode: '220',
  },
  {
    code: 'DJ',
    name: 'Djibouti',
    dialCode: '+253',
    flag: '🇩🇯',
    numericCode: '253',
  },
  {
    code: 'ER',
    name: 'Eritrea',
    dialCode: '+291',
    flag: '🇪🇷',
    numericCode: '291',
  },
  {
    code: 'KM',
    name: 'Comoros',
    dialCode: '+269',
    flag: '🇰🇲',
    numericCode: '269',
  },

  // ── Middle East ──
  {
    code: 'AE',
    name: 'United Arab Emirates',
    dialCode: '+971',
    flag: '🇦🇪',
    numericCode: '971',
  },
  {
    code: 'SA',
    name: 'Saudi Arabia',
    dialCode: '+966',
    flag: '🇸🇦',
    numericCode: '966',
  },
  {
    code: 'QA',
    name: 'Qatar',
    dialCode: '+974',
    flag: '🇶🇦',
    numericCode: '974',
  },
  {
    code: 'KW',
    name: 'Kuwait',
    dialCode: '+965',
    flag: '🇰🇼',
    numericCode: '965',
  },
  {
    code: 'BH',
    name: 'Bahrain',
    dialCode: '+973',
    flag: '🇧🇭',
    numericCode: '973',
  },
  {
    code: 'OM',
    name: 'Oman',
    dialCode: '+968',
    flag: '🇴🇲',
    numericCode: '968',
  },
  {
    code: 'JO',
    name: 'Jordan',
    dialCode: '+962',
    flag: '🇯🇴',
    numericCode: '962',
  },
  {
    code: 'IQ',
    name: 'Iraq',
    dialCode: '+964',
    flag: '🇮🇶',
    numericCode: '964',
  },
  {
    code: 'IL',
    name: 'Israel',
    dialCode: '+972',
    flag: '🇮🇱',
    numericCode: '972',
  },

  // ── Asia ──
  { code: 'IN', name: 'India', dialCode: '+91', flag: '🇮🇳', numericCode: '91' },
  {
    code: 'PK',
    name: 'Pakistan',
    dialCode: '+92',
    flag: '🇵🇰',
    numericCode: '92',
  },
  {
    code: 'BD',
    name: 'Bangladesh',
    dialCode: '+880',
    flag: '🇧🇩',
    numericCode: '880',
  },
  { code: 'CN', name: 'China', dialCode: '+86', flag: '🇨🇳', numericCode: '86' },
  {
    code: 'HK',
    name: 'Hong Kong',
    dialCode: '+852',
    flag: '🇭🇰',
    numericCode: '852',
  },
  { code: 'JP', name: 'Japan', dialCode: '+81', flag: '🇯🇵', numericCode: '81' },
  {
    code: 'KR',
    name: 'South Korea',
    dialCode: '+82',
    flag: '🇰🇷',
    numericCode: '82',
  },
  {
    code: 'SG',
    name: 'Singapore',
    dialCode: '+65',
    flag: '🇸🇬',
    numericCode: '65',
  },
  {
    code: 'MY',
    name: 'Malaysia',
    dialCode: '+60',
    flag: '🇲🇾',
    numericCode: '60',
  },
  {
    code: 'ID',
    name: 'Indonesia',
    dialCode: '+62',
    flag: '🇮🇩',
    numericCode: '62',
  },
  {
    code: 'PH',
    name: 'Philippines',
    dialCode: '+63',
    flag: '🇵🇭',
    numericCode: '63',
  },
  {
    code: 'TH',
    name: 'Thailand',
    dialCode: '+66',
    flag: '🇹🇭',
    numericCode: '66',
  },
  {
    code: 'VN',
    name: 'Vietnam',
    dialCode: '+84',
    flag: '🇻🇳',
    numericCode: '84',
  },

  // ── Europe ──
  {
    code: 'GB',
    name: 'United Kingdom',
    dialCode: '+44',
    flag: '🇬🇧',
    numericCode: '44',
  },
  {
    code: 'PT',
    name: 'Portugal',
    dialCode: '+351',
    flag: '🇵🇹',
    numericCode: '351',
  },
  { code: 'ES', name: 'Spain', dialCode: '+34', flag: '🇪🇸', numericCode: '34' },
  {
    code: 'FR',
    name: 'France',
    dialCode: '+33',
    flag: '🇫🇷',
    numericCode: '33',
  },
  {
    code: 'DE',
    name: 'Germany',
    dialCode: '+49',
    flag: '🇩🇪',
    numericCode: '49',
  },
  { code: 'IT', name: 'Italy', dialCode: '+39', flag: '🇮🇹', numericCode: '39' },
  {
    code: 'NL',
    name: 'Netherlands',
    dialCode: '+31',
    flag: '🇳🇱',
    numericCode: '31',
  },
  {
    code: 'BE',
    name: 'Belgium',
    dialCode: '+32',
    flag: '🇧🇪',
    numericCode: '32',
  },
  {
    code: 'CH',
    name: 'Switzerland',
    dialCode: '+41',
    flag: '🇨🇭',
    numericCode: '41',
  },
  {
    code: 'SE',
    name: 'Sweden',
    dialCode: '+46',
    flag: '🇸🇪',
    numericCode: '46',
  },
  {
    code: 'NO',
    name: 'Norway',
    dialCode: '+47',
    flag: '🇳🇴',
    numericCode: '47',
  },
  {
    code: 'DK',
    name: 'Denmark',
    dialCode: '+45',
    flag: '🇩🇰',
    numericCode: '45',
  },
  {
    code: 'PL',
    name: 'Poland',
    dialCode: '+48',
    flag: '🇵🇱',
    numericCode: '48',
  },
  {
    code: 'RO',
    name: 'Romania',
    dialCode: '+40',
    flag: '🇷🇴',
    numericCode: '40',
  },
  {
    code: 'CZ',
    name: 'Czech Republic',
    dialCode: '+420',
    flag: '🇨🇿',
    numericCode: '420',
  },
  {
    code: 'HU',
    name: 'Hungary',
    dialCode: '+36',
    flag: '🇭🇺',
    numericCode: '36',
  },
  {
    code: 'IE',
    name: 'Ireland',
    dialCode: '+353',
    flag: '🇮🇪',
    numericCode: '353',
  },
  {
    code: 'TR',
    name: 'Turkey',
    dialCode: '+90',
    flag: '🇹🇷',
    numericCode: '90',
  },
  { code: 'RU', name: 'Russia', dialCode: '+7', flag: '🇷🇺', numericCode: '7' },
  {
    code: 'UA',
    name: 'Ukraine',
    dialCode: '+380',
    flag: '🇺🇦',
    numericCode: '380',
  },

  // ── Americas ──
  {
    code: 'US',
    name: 'United States',
    dialCode: '+1',
    flag: '🇺🇸',
    numericCode: '1',
  },
  { code: 'CA', name: 'Canada', dialCode: '+1', flag: '🇨🇦', numericCode: '1' },
  {
    code: 'MX',
    name: 'Mexico',
    dialCode: '+52',
    flag: '🇲🇽',
    numericCode: '52',
  },
  {
    code: 'BR',
    name: 'Brazil',
    dialCode: '+55',
    flag: '🇧🇷',
    numericCode: '55',
  },
  {
    code: 'AR',
    name: 'Argentina',
    dialCode: '+54',
    flag: '🇦🇷',
    numericCode: '54',
  },
  {
    code: 'CO',
    name: 'Colombia',
    dialCode: '+57',
    flag: '🇨🇴',
    numericCode: '57',
  },
  { code: 'PE', name: 'Peru', dialCode: '+51', flag: '🇵🇪', numericCode: '51' },
  { code: 'CL', name: 'Chile', dialCode: '+56', flag: '🇨🇱', numericCode: '56' },

  // ── Oceania ──
  {
    code: 'AU',
    name: 'Australia',
    dialCode: '+61',
    flag: '🇦🇺',
    numericCode: '61',
  },
  {
    code: 'NZ',
    name: 'New Zealand',
    dialCode: '+64',
    flag: '🇳🇿',
    numericCode: '64',
  },
];

// ── Smart Phone Number Parser ───────────────────────────

export interface ParsedPhoneResult {
  /** The sanitized E.164 phone number (e.g. "+260978373838") */
  formatted: string;
  /** Whether the input was valid enough to produce a result */
  valid: boolean;
  /** User-facing error message if invalid */
  error?: string;
}

function validateParsedPhone(phone: string): ParsedPhoneResult {
  const digits = phone.replace(/\D/g, '');
  if (digits.length < 7) {
    return {
      formatted: phone,
      valid: false,
      error: 'Phone number is too short',
    };
  }
  if (digits.length > 15) {
    return {
      formatted: phone,
      valid: false,
      error: 'Phone number is too long',
    };
  }
  if (!/^\+[1-9]\d{6,14}$/.test(phone)) {
    return {
      formatted: phone,
      valid: false,
      error: 'Invalid phone number format',
    };
  }
  return { formatted: phone, valid: true };
}

/**
 * Parse a raw user-entered phone string into E.164 format based on
 * the selected country's numeric dial code (e.g. "260" for Zambia).
 *
 * Handles these input formats:
 *   - "+260978373838" → already correct, keeps as-is
 *   - "978373838"      → local number, prepends country code
 *   - "0978373838"     → local trunk prefix, strips 0, prepends country code
 *   - "00260978373838" → international 00-prefix, replaces 00 with +
 *   - "+7837XXZZ"      → letters stripped, digits-only result
 *
 * @param rawInput    - Whatever the user typed
 * @param countryCode - The numeric country calling code (e.g. "260")
 */
export function parsePhoneNumber(
  rawInput: string,
  countryCode: string,
): ParsedPhoneResult {
  // 1. Strip ALL non-digit characters
  let digits = rawInput.replace(/\D/g, '');

  if (digits.length === 0) {
    return { formatted: '', valid: false, error: 'Phone number is required' };
  }

  // 2. "00" international dialing prefix → strip it, respect the rest
  if (digits.startsWith('00')) {
    digits = digits.slice(2);
    return validateParsedPhone(`+${digits}`);
  }

  // 3. User typed "+" prefix → already international format
  if (rawInput.trim().startsWith('+')) {
    return validateParsedPhone(`+${digits}`);
  }

  // 4. Strip local trunk prefix "0"
  if (digits.startsWith('0')) {
    digits = digits.slice(1);
  }

  // 5. Avoid double-adding country code
  if (digits.startsWith(countryCode)) {
    return validateParsedPhone(`+${digits}`);
  }

  // 6. Prepend selected country code
  return validateParsedPhone(`+${countryCode}${digits}`);
}

/**
 * Find a country by its ISO alpha-2 code (e.g. "ZM").
 */
export function findCountryByCode(code: string): CountryPhoneData | undefined {
  return SUPPORTED_COUNTRIES.find(
    (c) => c.code.toUpperCase() === code.toUpperCase(),
  );
}

/** Country code assigned to legacy global products by the backfill. */
export const DEFAULT_GLOBAL_PRODUCT_COUNTRY = 'MZ';

/**
 * Countries a product is sold in. Rows written before `countries` existed
 * only carry the scalar `country`, so a missing list reads as `[country]`
 * (or `[]` when neither is set). Values are returned as given (not
 * normalized); use `normalizeCountryCode` on each when a code is required.
 */
export function productCountries(product: {
  country?: string | null;
  countries?: string[] | null;
}): string[] {
  if (product.countries && product.countries.length > 0) {
    return product.countries;
  }
  return product.country ? [product.country] : [];
}

/** Local-language spellings mapped to canonical `SUPPORTED_COUNTRIES` names.
 * Accent folding alone cannot resolve these ("Moçambique" folds to
 * "mocambique", not "mozambique"). Keys are accent-folded lowercase. */
export const COUNTRY_NAME_ALIASES: Record<string, string> = {
  mocambique: 'Mozambique',
  'africa do sul': 'South Africa',
  quenia: 'Kenya',
  tanzania: 'Tanzania',
  zambia: 'Zambia',
  zimbabue: 'Zimbabwe',
  'cabo verde': 'Cape Verde',
  'guine-bissau': 'Guinea-Bissau',
  'sao tome e principe': 'São Tomé and Príncipe',
};

const foldCountryText = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();

/**
 * Resolve a country ISO alpha-2 code ("MZ"), canonical name ("Mozambique"),
 * or local variant ("Moçambique") to the canonical display name from
 * `SUPPORTED_COUNTRIES`. Comparison ignores case and accents. Unknown values
 * are returned trimmed as-is so nothing is silently dropped; empty input
 * returns undefined.
 */
export function normalizeCountryName(
  value: string | null | undefined,
): string | undefined {
  const trimmed = String(value ?? '').trim();
  if (!trimmed) return undefined;

  if (trimmed.length === 2) {
    const byCode = findCountryByCode(trimmed);
    if (byCode) return byCode.name;
  }

  const folded = foldCountryText(trimmed);
  const byName = SUPPORTED_COUNTRIES.find(
    (c) => foldCountryText(c.name) === folded,
  );
  if (byName) return byName.name;

  return COUNTRY_NAME_ALIASES[folded] ?? trimmed;
}

/**
 * Resolve a country to its canonical ISO 3166-1 alpha-2 code (e.g. "MZ").
 * Accepts a code ("MZ"), a canonical name ("Mozambique") or a local
 * variant ("Moçambique"). Unknown or empty values return undefined.
 */
export function normalizeCountryCode(
  value: string | null | undefined,
): string | undefined {
  const trimmed = String(value ?? '').trim();
  if (!trimmed) return undefined;

  const byCode = trimmed.length === 2 ? findCountryByCode(trimmed) : undefined;
  if (byCode) return byCode.code;

  const name = normalizeCountryName(trimmed);
  const byName = name
    ? SUPPORTED_COUNTRIES.find((c) => c.name === name)
    : undefined;
  return byName?.code;
}

/**
 * Guess the user's country from their locale string (e.g. "en-ZM" → Zambia).
 */
export function guessCountryFromLocale(
  locale?: string,
): CountryPhoneData | undefined {
  if (!locale) return undefined;
  const parts = locale.split('-');
  if (parts.length >= 2) {
    return findCountryByCode(parts[parts.length - 1]);
  }
  return undefined;
}

/**
 * Find a country by its display name (e.g. "Zambia" → ZM entry).
 * Accent-insensitive, and resolves known localized spellings.
 */
export function findCountryByName(
  name?: string | null,
): CountryPhoneData | undefined {
  if (!name) return undefined;
  const normalized = normalizeCountryName(name);
  if (!normalized) return undefined;
  return (
    SUPPORTED_COUNTRIES.find((c) => c.name === normalized) ??
    SUPPORTED_COUNTRIES.find(
      (c) => foldCountryText(c.name) === foldCountryText(normalized),
    )
  );
}

/**
 * ISO 3166-1 alpha-2 code for a merchant's `country` display name
 * (e.g. "Mozambique" → "MZ"). Undefined when the name is unknown —
 * callers should omit the country rather than guess one.
 */
export function countryCodeForCountryName(
  name?: string | null,
): string | undefined {
  return findCountryByName(name)?.code;
}

// ── Display currency resolution ──────────────────────────

/**
 * ISO 3166-1 alpha-2 country code → ISO 4217 currency code for every
 * `SUPPORTED_COUNTRIES` entry, so a merchant's or store's country can
 * stand in when its currency configuration is missing.
 */
export const CURRENCY_BY_COUNTRY_CODE: Record<string, string> = {
  // ── Africa ──
  ZM: 'ZMW',
  MZ: 'MZN',
  ZA: 'ZAR',
  ZW: 'ZWG',
  MW: 'MWK',
  TZ: 'TZS',
  KE: 'KES',
  UG: 'UGX',
  RW: 'RWF',
  BI: 'BIF',
  CD: 'CDF',
  CG: 'XAF',
  AO: 'AOA',
  NA: 'NAD',
  BW: 'BWP',
  SZ: 'SZL',
  LS: 'LSL',
  MG: 'MGA',
  MU: 'MUR',
  SC: 'SCR',
  ET: 'ETB',
  SO: 'SOS',
  SD: 'SDG',
  SS: 'SSP',
  EG: 'EGP',
  LY: 'LYD',
  TN: 'TND',
  DZ: 'DZD',
  MA: 'MAD',
  MR: 'MRU',
  SN: 'XOF',
  ML: 'XOF',
  GN: 'GNF',
  CI: 'XOF',
  BF: 'XOF',
  NE: 'XOF',
  TG: 'XOF',
  BJ: 'XOF',
  NG: 'NGN',
  GH: 'GHS',
  CM: 'XAF',
  GA: 'XAF',
  GQ: 'XAF',
  TD: 'XAF',
  CF: 'XAF',
  CV: 'CVE',
  ST: 'STN',
  GW: 'XOF',
  SL: 'SLE',
  LR: 'LRD',
  GM: 'GMD',
  DJ: 'DJF',
  ER: 'ERN',
  KM: 'KMF',
  // ── Middle East ──
  AE: 'AED',
  SA: 'SAR',
  QA: 'QAR',
  KW: 'KWD',
  BH: 'BHD',
  OM: 'OMR',
  JO: 'JOD',
  IQ: 'IQD',
  IL: 'ILS',
  // ── Asia ──
  IN: 'INR',
  PK: 'PKR',
  BD: 'BDT',
  CN: 'CNY',
  HK: 'HKD',
  JP: 'JPY',
  KR: 'KRW',
  SG: 'SGD',
  MY: 'MYR',
  ID: 'IDR',
  PH: 'PHP',
  TH: 'THB',
  VN: 'VND',
  // ── Europe ──
  GB: 'GBP',
  PT: 'EUR',
  ES: 'EUR',
  FR: 'EUR',
  DE: 'EUR',
  IT: 'EUR',
  NL: 'EUR',
  BE: 'EUR',
  CH: 'CHF',
  SE: 'SEK',
  NO: 'NOK',
  DK: 'DKK',
  PL: 'PLN',
  RO: 'RON',
  CZ: 'CZK',
  HU: 'HUF',
  IE: 'EUR',
  TR: 'TRY',
  RU: 'RUB',
  UA: 'UAH',
  // ── Americas ──
  US: 'USD',
  CA: 'CAD',
  MX: 'MXN',
  BR: 'BRL',
  AR: 'ARS',
  CO: 'COP',
  PE: 'PEN',
  CL: 'CLP',
  // ── Oceania ──
  AU: 'AUD',
  NZ: 'NZD',
};

/**
 * ISO 4217 currency code → display symbol for the POS UI (cart, checkout,
 * receipts, scale labels). Missing codes display as the code itself.
 *
 * ASCII-only on purpose: the native POS receipt formatter strips
 * non-ASCII characters for thermal printers, so symbols like '₦' or '₵'
 * would print as blanks. Only widely-unambiguous ASCII symbols are
 * listed; everything else falls back to its code.
 */
export const CURRENCY_SYMBOLS: Record<string, string> = {
  ZMW: 'K',
  MZN: 'MT',
  ZAR: 'R',
  USD: '$',
  MWK: 'MK',
  BWP: 'P',
  TZS: 'TSh',
  KES: 'KSh',
  UGX: 'USh',
  RWF: 'FRw',
  AOA: 'Kz',
  XAF: 'FCFA',
  XOF: 'FCFA',
};

/**
 * Currency code for a country given by ISO code, canonical name, or a
 * localized spelling ('ZM' | 'Zambia' → 'ZMW'). Undefined when unknown.
 */
export function currencyForCountry(
  country?: string | null,
): string | undefined {
  const code = normalizeCountryCode(country);
  return code ? CURRENCY_BY_COUNTRY_CODE[code] : undefined;
}

/**
 * Resolve the currency money should be DISPLAYED in, per the product rule:
 * an explicit code wins, then the merchant's configured currency, then the
 * currency of the merchant's country, then of the store's country_code.
 * Returns undefined when nothing is known so callers can fall back to the
 * module display default. A store's 'NA' country_code is the platform's
 * "not available" sentinel and never resolves as Namibia — a genuine
 * Namibian store resolves through its merchant's country instead.
 */
export function resolveDisplayCurrencyCode(args: {
  currencyCode?: string | null;
  merchant?: {
    currency?: { code?: string | null } | null;
    country?: string | null;
  } | null;
  store?: { country_code?: string | null } | null;
}): string | undefined {
  const explicit = args.currencyCode?.trim();
  if (explicit) return explicit.toUpperCase();

  // The merchant schema accepts arbitrary strings — a malformed code
  // (e.g. the symbol 'MT' stored as a code) falls through to the country
  // legs instead of reaching the formatters, where it would throw.
  const merchantCode = args.merchant?.currency?.code?.trim();
  if (merchantCode && isFormattableCurrencyCode(merchantCode)) {
    return merchantCode.toUpperCase();
  }

  const byMerchantCountry = currencyForCountry(args.merchant?.country);
  if (byMerchantCountry) return byMerchantCountry;

  const storeCountry = args.store?.country_code?.trim().toUpperCase();
  if (storeCountry && storeCountry !== 'NA') {
    return currencyForCountry(storeCountry);
  }
  return undefined;
}

/**
 * Module-level default used by the money formatters when a call supplies
 * no currency. It starts as 'MZN' — the long-standing behavior, which
 * backend bundles still rely on (e.g. service-shifts note text) — and is
 * overridden in the browser as soon as a merchant loads (shared-ui's
 * displayCurrencyEffect), so front ends always show the merchant's money
 * and a non-MZN merchant never sees MZN.
 */
const INITIAL_DISPLAY_CURRENCY_CODE = 'MZN';

let defaultDisplayCurrencyCode = INITIAL_DISPLAY_CURRENCY_CODE;

/**
 * True when Intl.NumberFormat can format with this currency code (three
 * ASCII letters, accepted by the engine). The merchant schema accepts
 * arbitrary strings, so anything else — e.g. a symbol like 'MT' stored as
 * a code — must never reach the formatters, where it would throw a
 * RangeError on every call.
 */
export function isFormattableCurrencyCode(code?: string | null): boolean {
  const trimmed = code?.trim();
  if (!trimmed || !/^[A-Za-z]{3}$/.test(trimmed)) return false;
  try {
    new Intl.NumberFormat('en', { style: 'currency', currency: trimmed });
    return true;
  } catch {
    return false;
  }
}

/**
 * The code when Intl can format it, else the given fallback. For call
 * sites that format a PERSISTED code and must keep their own fallback
 * (e.g. the platform billing constant) — passing a malformed code
 * straight to the formatters would fall back to the merchant display
 * default instead, mislabeling platform money.
 */
export function formattableCurrencyOr(
  code: string | null | undefined,
  fallback: string,
): string {
  return isFormattableCurrencyCode(code)
    ? (code as string).trim().toUpperCase()
    : fallback;
}

/** Set the display default; blank or non-formattable values leave the
 *  known-good default unchanged. */
export function setDefaultDisplayCurrency(code?: string | null): void {
  const trimmed = code?.trim();
  if (trimmed && isFormattableCurrencyCode(trimmed)) {
    defaultDisplayCurrencyCode = trimmed.toUpperCase();
  }
}

/**
 * Restore the pre-login display default. Called on sign-out or when the
 * signed-in account can no longer resolve a currency, so a previous
 * merchant's currency never leaks into another session.
 */
export function resetDefaultDisplayCurrency(): void {
  defaultDisplayCurrencyCode = INITIAL_DISPLAY_CURRENCY_CODE;
}

export function getDefaultDisplayCurrencyCode(): string {
  return defaultDisplayCurrencyCode;
}

/**
 * Auto-detect a country from a phone number's country code prefix.
 * Tries 3-digit, then 2-digit, then 1-digit codes against the known list.
 * Only matches when the input appears to be in international format
 * (starts with + or 00) or has enough digits to disambiguate.
 *
 * @param rawInput - Whatever the user typed
 * @returns The matching country, or undefined if no match
 */
export function detectCountryFromPhone(
  rawInput: string,
): CountryPhoneData | undefined {
  let digits = rawInput.replace(/\D/g, '');
  // Require at least 9 digits (full local number) before auto-detecting,
  // otherwise short inputs like "48337" would falsely match PL (+48)
  if (digits.length <= 9) return undefined;

  // Strip international prefixes
  const isInternational =
    rawInput.trim().startsWith('+') || digits.startsWith('00');
  if (digits.startsWith('00')) digits = digits.slice(2);

  // Try 3-digit codes first, then 2-digit, then 1-digit
  for (const len of [3, 2, 1]) {
    if (digits.length >= len) {
      const prefix = digits.slice(0, len);
      const country = SUPPORTED_COUNTRIES.find((c) => c.numericCode === prefix);
      if (country) {
        // For 1-digit matches (like +1 for US/CA), require it to be
        // clearly international to avoid false matches on local numbers
        if (len === 1 && !isInternational) continue;
        return country;
      }
    }
  }
  return undefined;
}

// ── Legacy phone helpers (kept for backward compatibility) ──

export function isValidPhoneNumber(phoneNumber: string): boolean {
  const e164Pattern = /^\+[1-9]\d{1,14}$/;
  return e164Pattern.test(phoneNumber.trim());
}

export function toValidPhoneNumber(phoneNumber: string): string {
  const digitsOnly = phoneNumber.replace(/\D/g, '');
  if (
    digitsOnly.length > 0 &&
    digitsOnly.length <= 15 &&
    /^[1-9]/.test(digitsOnly)
  ) {
    return `+${digitsOnly}`;
  }
  return '';
}

export function isValidPin(pin: string): boolean {
  // Define a regular expression pattern for a 4-digit pin
  const _4pinPattern = /^\d{4}$/;
  const _5pinPattern = /^\d{5}$/;
  const _6pinPattern = /^\d{6}$/;

  // Test the pin against the pattern
  return (
    _4pinPattern.test(pin) || _5pinPattern.test(pin) || _6pinPattern.test(pin)
  );
}

export const reverseString = (str: string): string => {
  return str.split('').reverse().join('');
};

export const getLastNCharacters = (str: string, n: number): string => {
  return str.slice(-n);
};

export const cleanObject = (obj: any) => {
  const newObj: { [key: string]: any } = Array.isArray(obj) ? [] : {};

  for (const propName in obj) {
    const value = obj[propName];
    if (value === null || value === undefined || value === '') {
      continue;
    } else if (typeof value === 'object' && !Array.isArray(value)) {
      const cleanedObj = cleanObject(value);
      if (Object.keys(cleanedObj).length > 0) {
        newObj[propName] = cleanedObj;
      }
    } else {
      newObj[propName] = value;
    }
  }

  return newObj;
};

export const isValidDate = (date: string): boolean => {
  return typeof date === 'string' && !isNaN(Date.parse(date));
};

/**
 * Returns the start of the day in UTC for the given date.
 * @param d - The date to get the start of day for.
 * @returns A new Date object set to 00:00:00.000 UTC of the given date.
 */
export const startOfDayUTC = (d: Date): Date =>
  new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));

/**
 * Returns the end of the day in UTC for the given date.
 * @param d - The date to get the end of day for.
 * @returns A new Date object set to 23:59:59.999 UTC of the given date.
 */
export const endOfDayUTC = (d: Date): Date =>
  new Date(
    Date.UTC(
      d.getUTCFullYear(),
      d.getUTCMonth(),
      d.getUTCDate(),
      23,
      59,
      59,
      999,
    ),
  );

/**
 * Adds the specified number of days to a date in UTC.
 * @param d - The date to add days to.
 * @param days - The number of days to add.
 * @returns A new Date object with the days added.
 */
export const addDaysUTC = (d: Date, days: number): Date => {
  const x = new Date(d);
  x.setUTCDate(x.getUTCDate() + days);
  return x;
};

/**
 * Adds the specified number of months to a date in UTC.
 * Handles month overflow correctly (e.g., Jan 31 + 1 month = Feb 28/29).
 * @param d - The date to add months to.
 * @param months - The number of months to add.
 * @returns A new Date object with the months added.
 */
export const addMonthsUTC = (d: Date, months: number): Date => {
  const year = d.getUTCFullYear();
  const month = d.getUTCMonth();
  const day = d.getUTCDate();

  const targetMonth = month + months;
  const lastDayOfTargetMonth = new Date(
    Date.UTC(year, targetMonth + 1, 0),
  ).getUTCDate();

  return new Date(
    Date.UTC(
      year,
      targetMonth,
      Math.min(day, lastDayOfTargetMonth),
      d.getUTCHours(),
      d.getUTCMinutes(),
      d.getUTCSeconds(),
      d.getUTCMilliseconds(),
    ),
  );
};

/**
 * Calculates a billing window starting from the given date.
 * The window starts at the start of the given day (UTC) and ends
 * at the last millisecond before the same day next month (UTC).
 * @param start - The start date for the billing window.
 * @returns An object containing startUTC and endUTC dates.
 */
export const billingWindow = (
  start: Date,
): { startUTC: Date; endUTC: Date } => {
  const startUTC = startOfDayUTC(start);
  const endUTC = new Date(addMonthsUTC(startUTC, 1).getTime() - 1);
  return { startUTC, endUTC };
};

export const isDate = (date: string): boolean => {
  return !isNaN(Date.parse(date));
};

export const flattenFirstLevel = (obj: any) => {
  const flattenedObj: { [key: string]: any } = {};

  for (const propName in obj) {
    if (
      typeof obj[propName] === 'object' &&
      !Array.isArray(obj[propName]) &&
      obj[propName]
    ) {
      for (const key in obj[propName]) {
        flattenedObj[key] = obj[propName][key];
      }
    } else {
      flattenedObj[propName] = obj[propName];
    }
  }

  return flattenedObj;
};

export function getISOWeekNumber(date: Date): number {
  // Copy date so don't modify original
  const dateCopy = new Date(date.getTime());
  // Set to nearest Thursday: current date + 4 - current day number, make Sunday's day number 7
  dateCopy.setDate(dateCopy.getDate() + 4 - (dateCopy.getDay() || 7));
  // Get first day of year
  const yearStart = new Date(dateCopy.getFullYear(), 0, 1);
  // Calculate full weeks to nearest Thursday
  const weekNo = Math.ceil(
    ((dateCopy.getTime() - yearStart.getTime()) / 86400000 + 1) / 7,
  );
  return weekNo;
}

export function isJsonObject(obj: any): boolean {
  try {
    JSON.parse(JSON.stringify(obj));
    return true;
  } catch (error) {
    return false;
  }
}

export function replaceBooleanStrings(obj: any): any {
  if (Array.isArray(obj)) {
    return obj.map(replaceBooleanStrings);
  } else if (obj !== null && typeof obj === 'object') {
    const newObj: any = {};
    for (const [key, value] of Object.entries(obj)) {
      newObj[key] = replaceBooleanStrings(value);
    }
    return newObj;
  } else if (typeof obj === 'string') {
    if (obj.toLowerCase() === 'true') return true;
    if (obj.toLowerCase() === 'false') return false;
  }
  return obj;
}

export function processBooleanStringsInJSON(obj: any): any {
  if (!isJsonObject(obj)) {
    return obj;
  }
  return replaceBooleanStrings(obj);
}

/**
 * Returns a new query string with only the key value pairs that have a value
 * @param queryStringKeyVPairs - key value pairs of param name to param value Record<string, string | null | undefined | number | boolean | string[] | number[] | boolean[]>
 * @returns
 * @example objectToQueryString({ cursor: '123', limit: '10', show_deleted: 'false', suppliers_ids: ['1', '2', '3'] })
 * // which returns 'cursor=123&limit=10&show_deleted=false&suppliers_ids=1,2,3'
 */
export function objectToQueryString(
  queryStringKeyVPairs: Record<
    string,
    | string
    | null
    | undefined
    | number
    | boolean
    | string[]
    | number[]
    | boolean[]
  >,
): string {
  const entries = Object.entries(queryStringKeyVPairs);

  let newQueryString = '?';
  let firstPairAdded = false;

  for (const [key, value] of entries) {
    if (
      value === undefined ||
      value === null ||
      value === '' ||
      value === 'undefined' ||
      value === 'null'
    ) {
      continue;
    }

    let valueString: string;
    if (Array.isArray(value)) {
      // Convert each element to string and join with commas, encode each element individually
      valueString = value.map((v) => encodeURIComponent(String(v))).join(',');
    } else {
      // Convert value to string and encode it
      valueString = encodeURIComponent(String(value));
    }

    if (firstPairAdded) {
      newQueryString += '&';
    }

    newQueryString += `${key}=${valueString}`;
    firstPairAdded = true;
  }

  return newQueryString;
}

/**
 * Number-formatting locale per currency code. The merchant's currency is
 * the only country signal these formatters receive, so it drives digit
 * grouping and symbol placement: lusophone currencies keep the original
 * 'pt' formatting (the primary market's), every other currency formats
 * with the neutral 'en' conventions instead of inheriting Mozambique's.
 */
const CURRENCY_FORMAT_LOCALES: Record<string, string> = {
  MZN: 'pt',
  AOA: 'pt',
  CVE: 'pt',
  STN: 'pt',
  BRL: 'pt-BR',
};

const localeForCurrency = (currencyCode: string): string =>
  CURRENCY_FORMAT_LOCALES[currencyCode.toUpperCase()] ?? 'en';

/**
 * Formats a given amount as currency.
 *
 * `whole` (the default) shows the amount as a whole number — the display
 * for FINAL amounts, which are always whole currency units in this system.
 * Pass `whole = false` for CALCULATION values (unit costs, intermediate
 * figures) that legitimately carry 2 decimals.
 * @param amount - The amount to format as currency.
 * @returns The formatted currency string.
 */
export const formatCurrency = (
  amount?: number,
  whole: boolean = true,
  currencyCode?: string,
) => {
  // Persisted codes can be arbitrary strings (e.g. a legacy 'MT' symbol
  // stored as a code); anything Intl cannot format falls back to the
  // known-good display default instead of throwing.
  const finalCurrencyCode = isFormattableCurrencyCode(currencyCode)
    ? (currencyCode as string).trim().toUpperCase()
    : getDefaultDisplayCurrencyCode();
  return new Intl.NumberFormat(localeForCurrency(finalCurrencyCode), {
    style: 'currency',
    maximumFractionDigits: whole == true ? 0 : 2,
    currency: finalCurrencyCode,
  }).format(amount ?? 0);
};

export const formatCurrencyWithCurrencyCode = (
  amount: number,
  currencyCode?: string,
  whole?: boolean,
) => {
  const finalCurrencyCode = isFormattableCurrencyCode(currencyCode)
    ? (currencyCode as string).trim().toUpperCase()
    : getDefaultDisplayCurrencyCode();
  return new Intl.NumberFormat(localeForCurrency(finalCurrencyCode), {
    style: 'currency',
    maximumFractionDigits: whole == true ? 0 : 2,
    currency: finalCurrencyCode,
  }).format(amount ?? 0);
};

/**
 * Canonical FINAL-amount display: whole currency units (final amounts are
 * always whole in this system), pre-rounded half-up with the same rule the
 * backend settles by, using the merchant currency's code for the symbol
 * (falls back to the display default set at merchant load). What the user
 * sees is exactly the amount the system settles against.
 */
export const formatMoney = (
  amount?: number | null,
  currency?: { code?: string | null; decimal_places?: number | null } | null,
) => {
  const code = isFormattableCurrencyCode(currency?.code)
    ? (currency!.code as string).trim().toUpperCase()
    : getDefaultDisplayCurrencyCode();
  return new Intl.NumberFormat(localeForCurrency(code), {
    style: 'currency',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    currency: code,
  }).format(roundFinalMoney(amount ?? 0));
};

export function isStringifiedJSON(str: string): boolean {
  try {
    // Attempt to parse the string using JSON.parse
    JSON.parse(str);
    return true;
  } catch (e) {
    // If an error is thrown, it means that the string is not a valid JSON
    return false;
  }
}

export function axiosRetryConfig(axios: AxiosStatic, retries?: number) {
  // Custom exponential delay function with an initial delay of 1 second (1000 milliseconds)
  const customExponentialDelay = (retryCount: number) => {
    const baseDelay = 1000; // 1 second in milliseconds
    return baseDelay * Math.pow(2, retryCount - 1);
  };

  // Configure axios-retry with the custom delay, retry condition, and logging
  axiosRetry(axios, {
    retries: retries ?? 2,
    retryDelay: customExponentialDelay,
    retryCondition: (error) => {
      // A 504 is API Gateway giving up after 29 s: resending the same
      // request only repeats the timeout and the load behind it.
      if (error.response?.status === 504) return false;
      const shouldRetry =
        axiosRetry.isNetworkOrIdempotentRequestError(error) ||
        (error.response && error.response.status >= 500);
      return shouldRetry === undefined ? false : shouldRetry;
    },
    onRetry: (retryCount, error, requestConfig) => {
      console.log(`Retry attempt ${retryCount}`);
      console.log(`URL: ${requestConfig.url}`);
      if (error.response) {
        console.log(`Status Code: ${error.response.status}`);
        console.log(`Response Data: ${JSON.stringify(error.response.data)}`);
      } else {
        console.log(`Error Message: ${error.message}`);
      }
    },
  });
}

export const constructStoreSKU = ({
  global_sku,
  store_prefix,
}: {
  global_sku: number;
  store_prefix: string;
}): string => {
  if (!global_sku) {
    throw new Error('Global SKU is required');
  }

  if (global_sku < 0) {
    throw new Error('Global SKU must be a positive number');
  }

  if (!store_prefix) {
    throw new Error('Store prefix is required');
  }

  return `${global_sku}${store_prefix}`.toUpperCase();
};

/**
 * Simplifies a fraction given a numerator and denominator.
 *
 * This function computes the greatest common divisor (GCD) of the numerator and denominator
 * using the Euclidean algorithm, divides both by the GCD, and returns the simplified fraction
 * as a string in the format "Nsimplified/Dsimplified".
 *
 * @param n - The numerator of the fraction.
 * @param d - The denominator of the fraction. Must not be zero.
 * @returns The simplified fraction as a string.
 * @throws Will throw an error if the denominator is zero.
 */
export function simplifyFraction(n: number, d: number): string {
  if (d === 0) {
    throw new Error('Denominator cannot be zero');
  }

  /**
   * Computes the greatest common divisor (GCD) of two numbers using the Euclidean algorithm.
   *
   * @param a - The first number.
   * @param b - The second number.
   * @returns The greatest common divisor of `a` and `b`.
   */
  function gcd(a: number, b: number): number {
    return b === 0 ? Math.abs(a) : gcd(b, a % b);
  }

  const commonDivisor = gcd(n, d);
  let simplifiedN = n / commonDivisor;
  let simplifiedD = d / commonDivisor;

  // Ensure the denominator is positive.
  if (simplifiedD < 0) {
    simplifiedN = -simplifiedN;
    simplifiedD = -simplifiedD;
  }

  return `${simplifiedN}/${simplifiedD}`;
}

type SortDirection = 'asc' | 'desc' | boolean;

export function sortByPriority<T, V>(
  items: T[],
  attributePath: string | Array<string | number>,
  priorityOrder: V[],
  directions: SortDirection[] = ['asc'],
): T[] {
  // Build a map from each prioritized value to its index
  const orderMap = new Map<V, number>(
    priorityOrder.map((value, idx) => [value, idx]),
  );
  const maxOrder = priorityOrder.length;

  return orderBy(
    items,
    [
      (item: T) => {
        // safely grab nested value
        const val = get(item, attributePath) as V;
        // look up its rank, fallback to maxOrder
        return orderMap.has(val) ? orderMap.get(val)! : maxOrder;
      },
    ],
    directions,
  );
}

export const getAPIErrorMessage = (error: any): string => {
  // Handle Zod validation errors
  if (error?.response?.data?.message) {
    const message = error.response.data.message;

    // If message is an array (Zod validation format)
    if (Array.isArray(message)) {
      // Extract all validation errors
      const errors = message.map((item: any) => {
        if (typeof item === 'string') return item;
        if (typeof item === 'object') {
          // Get all field errors from the object
          return Object.entries(item)
            .map(([field, error]) => `${field}: ${error}`)
            .join(', ');
        }
        return JSON.stringify(item);
      });

      // Join all errors with semicolons for readability
      return errors.filter(Boolean).join('; ') || 'Validation error occurred';
    }

    // If it's a string, return it
    if (typeof message === 'string') {
      return message;
    }

    // If it's an object but not an array
    if (typeof message === 'object') {
      // Check if it has specific error fields
      if (message.error) return String(message.error);
      if (message.detail) return String(message.detail);
      // Extract field-specific errors
      const fieldErrors = Object.entries(message)
        .map(([field, error]) => `${field}: ${error}`)
        .join(', ');
      if (fieldErrors) return fieldErrors;
      // Last resort: stringify
      return JSON.stringify(message);
    }
  }

  // Check if error.message is an array (alternative format)
  if (Array.isArray(error?.message)) {
    return error.message
      .map((item: any) => {
        if (typeof item === 'string') return item;
        if (typeof item === 'object') {
          return Object.entries(item)
            .map(([field, error]) => `${field}: ${error}`)
            .join(', ');
        }
        return JSON.stringify(item);
      })
      .join('; ');
  }

  // Fallback to error.message or default
  return error?.message || 'An unexpected error occurred';
};

export function languageForCountry(
  country: string | null | undefined,
): Language {
  // Accent-insensitive so localized spellings ("Moçambique") match too.
  switch (
    country
      ? foldCountryText(normalizeCountryName(country) ?? country)
      : undefined
  ) {
    case 'zambia':
    case 'zimbabwe':
    case 'malawi':
    case 'tanzania':
    case 'uganda':
    case 'south africa':
    case 'botswana':
    case 'kenya':
      return Language.EN_US;
    case 'mozambique':
    case 'mocambique':
    case 'angola':
    case 'guinea-bissau':
    case 'guine-bissau':
    case 'cape verde':
    case 'cabo verde':
    case 'sao tome and principe':
    case 'sao tome e principe':
      return Language.PT_PT;
    default:
      return Language.EN_US;
  }
}

/**
 * The merchant's default interface language (users who have not picked
 * one), derived from its country.
 */
export const uiLanguageForCountry = (
  country: string | null | undefined,
): UiLanguage =>
  languageForCountry(country) === Language.PT_PT
    ? UiLanguage.PT_PT
    : UiLanguage.EN;
